import type { AddOnQuantities, CartItem, Order, Service, VehicleType } from "@/lib/types";

const REORDER_KEY = "reorder_items";

/**
 * Store cart items in sessionStorage so the Booking page can pick them up
 * and pre-fill the cart (used by the Re-order flow).
 */
export function setReorderItems(items: CartItem[]): void {
  sessionStorage.setItem(REORDER_KEY, JSON.stringify(items));
}

/**
 * Read and CLEAR the reorder items. Returns null if none stored.
 * One-shot: once consumed, the key is removed (mirrors Booking's URL-clear pattern).
 */
export function getReorderItems(): CartItem[] | null {
  const raw = sessionStorage.getItem(REORDER_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(REORDER_KEY);
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed as CartItem[];
    return null;
  } catch {
    return null;
  }
}

/**
 * Build a fresh cart from a past order, recomputing prices/durations from the
 * CURRENT services list (so prices are up-to-date, not stale).
 *
 * - Multi-item orders: maps each order.items entry.
 * - Legacy single-item orders (no items[]): falls back to order.serviceId + vehicleType.
 * - Services that no longer exist or are inactive are skipped.
 */
export function buildCartFromOrder(order: Order, services: Service[] | undefined): CartItem[] {
  if (!services || services.length === 0) return [];

  const findService = (id: string) =>
    services.find((s) => s.id === id || (s as any)._id === id);

  const buildItem = (
    serviceId: string,
    vehicleType: VehicleType,
    quantity: number,
    unit?: number,
    addOns?: string[],
    addOnQuantities?: AddOnQuantities,
  ): CartItem | null => {
    const service = findService(serviceId);
    if (!service) return null;

    const vp = (service.vehiclePricing || {}) as Record<string, number | undefined>;
    const isPerUnit = !!service.isPerUnitPricing;

    // For per-unit services, the price is the service's base price per unit.
    // For regular services, look up the vehicle-specific price (fallback to service.price).
    const unitPrice = isPerUnit
      ? service.price
      : (vp[vehicleType] ?? service.price);

    return {
      serviceId,
      vehicleType,
      quantity: Math.max(1, quantity || 1),
      ...(unit !== undefined && unit ? { unit } : {}),
      ...(addOns && addOns.length > 0
        ? { addOns, ...(addOnQuantities ? { addOnQuantities } : {}) }
        : {}),
      serviceName: service.nameEn,
      serviceNameAr: service.nameAr,
      unitPrice,
      subtotal: unitPrice * (quantity || 1),
      ...(isPerUnit ? { isPerUnitPricing: true } : {}),
      duration: service.duration,
    };
  };

  const items: CartItem[] = [];

  if (order.items && order.items.length > 0) {
    for (const oi of order.items) {
      const item = buildItem(
        oi.serviceId,
        oi.vehicleType,
        oi.quantity,
        oi.unit,
        oi.addOns,
        oi.addOnQuantities,
      );
      if (item) items.push(item);
    }
  } else if (order.serviceId && order.vehicleType) {
    // Legacy single-item order
    const item = buildItem(order.serviceId, order.vehicleType as VehicleType, 1);
    if (item) items.push(item);
  }

  return items;
}
